/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보 결과 목록 관심표명자 컴포넌트
 * 버전 : 1.0
 * 파일명 : PromoInterested.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-17, 강동형 : 저작권 추가, 필터 바 추가, 레이아웃 변경에 따른 Props 추가, 총갯수 및 정렬버튼 삭제
 * 수정 : 2025-07-18, 강동형 : 리스트 배경색 추가
 *=================================================================
 */

import React, {useCallback, useEffect, useRef, useState} from 'react'
import { styled } from '@mui/material'
import InputBoxSearch from 'src/components/inputbox/InputBoxSearch';
import { promoResultType } from 'src/types/PromoType'
import PromoInterestedList from './PromoInterestedList';
import PromoFilterInterested from './PromoResultInterestDialog';
import { usePromoDialogInterest } from 'src/stores/PromoStore';
import { NextButton, NextButtonWrapper } from '../common/CommonStyled';
import { PromoResultType } from 'src/pages/PromoResultPage';
import { LoadPromoInterest } from './useSubmitPromoFilter';
import FilterBarPromoInterested from './FilterBarPromoInterested';


const SearchContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '10px',

    padding:'10px',
    width:'100%',
    zIndex:2,
    backgroundColor:'white',
    borderBottom:'1px solid #DDDDDD'
  }));
  
const SearchBox = styled('div')(({ theme }) => ({
    zIndex: '10',
    position: 'relative',
    display: 'flex',
    width:'100%',
    height:'46px'
  }));

const ListWrapper = styled('div')<{ hasAnyTag: boolean }>(({ hasAnyTag }) => ({
  display:'flex',
  flexDirection:'column',
  height: hasAnyTag
    ? 'calc(100vh - 593px)'
    : 'calc(100vh - 572px)',
  backgroundColor:'#f4f4f4'
}))

const PageWrapper = styled('div')({
  maxHeight: 'calc(100vh - 440px)'
})

interface Props{
  currentPromotion: promoResultType | undefined
  setTab : React.Dispatch<React.SetStateAction<PromoResultType>>;
  hasAnyTag : boolean
}

const PromoInterested = ({currentPromotion, setTab, hasAnyTag}:Props) => {
  const data = usePromoDialogInterest((state)=>state.response)
  // const total_rows = usePromoDialogInterest((state)=>state.total_rows)
  const setField = usePromoDialogInterest((state)=>state.setField)
  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortCriterion, setSortCriterion] = useState('최신순');
  const [render,setRender] = useState<any[]>([])
  const [search,setSearch] = useState("")
  const schoolName = currentPromotion?.school?.school_name
  const school_class = currentPromotion?.school?.education_class === '고등학교' ? 'high' : currentPromotion?.school?.education_class === '대학교' ? 'univ':'etc'
  const result_id = currentPromotion?.result_id ?? 0

  useEffect(() => {
    if(data){
      setRender(data);
    }
    }, [data]);
  useEffect(()=>{
    handleClose('input_date','desc','최신순')
  },[])
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) {
      (async () => {
        await LoadPromoInterest(result_id,school_class)
      })();
    } else {
      didMount.current = true;
    }
  }, [sortCriterion]);

  // 필터 버튼 클릭 이벤트 핸들러
  // const handleClick = (event: React.MouseEvent<HTMLElement>) => {
  //     setAnchorEl(event.currentTarget);
  // };

  // 필터 메뉴 닫기 이벤트 핸들러
  const handleClose = (sort_by:string, sort_order:string, text:string) => {
    setField('sort_by',sort_by)
    setField('sort_order',sort_order)
    setSortCriterion(text)
  };
    
  const onClickRegister = ()=>{
    setTab('editInterest')
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>)=>{
    setSearch(e.target.value)
    setField('student_name',e.target.value)
  }

  const handleSubmit = async () => {
    await LoadPromoInterest(result_id,school_class);
  };
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [refReady, setRefReady] = useState(false);
      
  const setLoadMoreRef = useCallback((node: HTMLDivElement | null) => {
    loadMoreRef.current = node;
    if (node && containerRef.current) setRefReady(true);
    else setRefReady(false);
  }, []);
  
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node && loadMoreRef.current) setRefReady(true);
    else setRefReady(false);
  }, []);
  
  useEffect(() => {
    if (!refReady) {
      return;
    }
  
    const target = loadMoreRef.current;
    const root = containerRef.current;
  
    if (!target || !root) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          (async () => {
          if (data.length !== 0) {
            await LoadPromoInterest(result_id,school_class,false)
          } else {

            await LoadPromoInterest(result_id,school_class,true)
          }
          })();
        }
      },
      {
        root: null,
        threshold: 0.1,
      }
    );
  
    observer.observe(target);
    observerRef.current = observer;
  
    return () => {
      observer.disconnect();
      setRefReady(false);
    };
  }, [refReady]);

  return (
    <>
    <PageWrapper>
      <SearchContainer>
        <SearchBox>
          <InputBoxSearch placeholder='검색' value={search} onChange={handleSearch} onClick={handleSubmit}/>
        </SearchBox>
        <div style={{width:'100%', overflowX:'auto'}} className='hide-scrollbar'>
          <FilterBarPromoInterested result_id={result_id} school_class={school_class}/>
        </div>
      </SearchContainer>
      <PromoFilterInterested/>
      <ListWrapper hasAnyTag={hasAnyTag}>
            {/* <SummaryWrapper>
                <Typography variant="body2" sx ={{color:'#7F7F89', fontSize:'12px', fontWeight:500}}>
                    총 <span style={{color:'black',fontSize:'16px'}}>{total_rows} </span>건
                    </Typography>
                <div>
                <FilterButton onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
                    {sortCriterion}
                </FilterButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => handleClose('input_date','desc','최신순')}>최신순</MenuItem>
                    <MenuItem onClick={() => handleClose('input_date','asc','과거순')}>과거순</MenuItem>
                    <MenuItem onClick={() => handleClose('name','asc','이름순')}>이름순</MenuItem>
                    <MenuItem onClick={() => handleClose('name','desc','이름역순')}>이름역순</MenuItem>
                    <MenuItem onClick={() => handleClose('birthday','desc','최근출생순')}>최근출생순</MenuItem>
                    <MenuItem onClick={() => handleClose('birthday','asc','과거출생순')}>과거출생순</MenuItem>
                </Menu>
                </div>
            </SummaryWrapper> */}
        <div style={{overflowY: 'auto',display: 'flex',flexDirection: 'column',height:'100%'}} ref={setContainerRef}>
          {render?.map((items, index) => (
            <PromoInterestedList
              listed={items}
              schoolName={schoolName}
              key={index}
              setTab={setTab}
              isLast={render.length-1 === index}
            />
          ))}
          <div ref={setLoadMoreRef} style={{height:'1px'}}/>
        </div>
      </ListWrapper>
    </PageWrapper>
    <NextButtonWrapper style={{boxShadow: '0px -3px 8px 0px rgba(0, 0, 0, 0.03)'}}>
      <NextButton onClick={onClickRegister}>
          관심표명자 추가
      </NextButton>
    </NextButtonWrapper>
    </>
  )
}

export default PromoInterested